import React, { useState, useMemo, useRef } from "react";
import { Button, Modal, Form, DatePicker, Row, Col, Select, message, Typography, Tag, Tooltip, Checkbox } from "antd";
import { FieldNumberOutlined, TableOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import moment from "moment";
import { isValid, isValidArray, isValidArrayWithValues, isValidString, checkActionAllowed } from "../../utils/utilities";
import { getAPIDateFormat, getLocalDate } from "../../utils/date.utils";
import { loadingAction } from "../../actions/loading-actions";
import { fetchSchedules } from "../../actions/home.action";
import { transferChartToNewTrip, getChartTransferMappingDetails } from "../../actions/admin.action";
import { showConfirm } from "../custom-components/custom-component";
import { postCall } from "../../utils/network.util";
import { FETCH_SEAT_LAYOUT_URL } from "../../utils/api-url";
import { VIEW_ADMIN_TAB_IN_SEARCH_PAGE } from "../../utils/constant";
import { getSeatGrid } from "../seat-layout/seat-layout-container.module";
import { generateSeatLayout } from "../seat-layout/seat-layout.module";
import { SeatContainer } from "../seat-layout/seat-layout.component";
import { SeatLegend } from "../seat-layout/seat-legend.component";
import '../seat-layout/seats.style.scss';
import './chart-transfer.style.scss';

const { Text } = Typography;
const { Option } = Select;

const isOccupiedSeat = (seat) => {
    return isValid(seat.seat_status) && (seat.seat_status === 'Confirmed' || seat.seat_status === 'Blocked');
};

const isVacantSeat = (seat) => {
    return seat.seat_number !== 'Empty' && seat.seat_number !== 'empty'
        && (!isValidString(seat.seat_status) || seat.seat_status === '');
};

const fetchSeatLayoutDirect = (tripRouteId) => {
    return postCall(FETCH_SEAT_LAYOUT_URL, {
        tripRouteId,
        isPassengerInfoNeeded: global.privilege === 'Redbus' ? false : true,
        agentId: global.agentId,
        operatorCode: global.operatorCode
    }).then((response) => {
        if (response && response.status === 'SUCCESS' && response.payload && response.payload.Status === 'SUCCESS') {
            return response.payload.Payload;
        }
        throw new Error((response && response.payload && response.payload.Message) || 'Failed to fetch seat layout');
    });
};

const SeatLayoutPanel = ({ seats, role, selectedOldSeat, mappings, onSeatClick }) => {
    const grid = useMemo(() => {
        if (!isValidArrayWithValues(seats)) return null;
        const isOldRole = role === 'old';

        // Optional (extra/not-boarded) mappings have no seat on the OLD layout
        // - their old "seat" is 'extra' or an already-released number that
        // another ticket may occupy now, so they must not highlight there. On
        // the new layout they occupy their target seat like any other mapping.
        const oldRoleMappings = mappings.filter((m) => !isValid(m.assignmentType) || m.assignmentType === 'seat');
        const oldSelectedSeats = isOldRole
            ? oldRoleMappings.map((m) => m.oldSeatNumber)
            : mappings.map((m) => m.newSeatNumber);

        // For new layout: overlay each mapped target seat with the source seat's
        // visual properties (status, gender, blocked_by) so it renders with the
        // same colour as the source seat. Click handling for the new layout still
        // gates by the original vacant status (kept on `__originalSeatStatus`).
        const renderSeats = !isOldRole
            ? seats.map((s) => {
                const m = mappings.find((mp) => mp.newSeatNumber === s.seat_number);
                if (!m) return s;
                return {
                    ...s,
                    __originalSeatStatus: s.seat_status,
                    seat_status: m.ticketStatus,
                    gender: m.gender,
                    blocked_by: global.userName,
                    ticket_id: m.ticketId
                };
            })
            : seats;

        const handleClick = (seat) => {
            if (isOldRole) {
                if (!isOccupiedSeat(seat)) return;
            } else {
                const wasVacant = isValid(seat.__originalSeatStatus)
                    ? !isValidString(seat.__originalSeatStatus) || seat.__originalSeatStatus === ''
                    : isVacantSeat(seat);
                if (!wasVacant) return;
            }
            onSeatClick(seat);
        };

        const result = getSeatGrid(
            renderSeats,
            handleClick,
            {},
            false,
            false,
            isOldRole,
            global?.travelsDetails?.both_decks_together,
            oldSelectedSeats,
            () => {},
            false
        );
        return result.seatGridLayout;
    }, [seats, role, selectedOldSeat, mappings, onSeatClick]);

    if (!grid) {
        return <div className="ct-helper-text">No seats to display.</div>;
    }

    const lowerCols = grid.lowerSeatGrid && grid.lowerSeatGrid.length > 1
        ? generateSeatLayout(grid.lowerSeatGrid, grid.maximumRowNumberByColumnForLower, grid.col_count)
        : [];
    const upperCols = grid.upperSeatGrid && grid.upperSeatGrid.length > 1
        ? generateSeatLayout(grid.upperSeatGrid, grid.maximumRowNumberByColumnForUpper, grid.col_count)
        : [];

    return (
        <div id="seat-layout">
            <div className="seat-layout-parent-container">
                {lowerCols.length > 0 && (
                    <SeatContainer deck="lower deck" seats={lowerCols} showDeckName={upperCols.length > 0} />
                )}
                {upperCols.length > 0 && (
                    <SeatContainer deck="upper deck" seats={upperCols} showDeckName={true} />
                )}
            </div>
        </div>
    );
};

export const ChartTransferComponent = (props) => {
    const { schedule } = props;

    const dispatch = useDispatch();
    const [form] = Form.useForm();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [availableTrips, setAvailableTrips] = useState([]);
    const [selectedNewTrip, setSelectedNewTrip] = useState(null);
    const [searched, setSearched] = useState(false);
    const [oldSeats, setOldSeats] = useState([]);
    const [newSeats, setNewSeats] = useState([]);
    const [selectedOldSeat, setSelectedOldSeat] = useState(null);
    const [mappings, setMappings] = useState([]);
    // Extra-seat / not-boarded passenger picked for an optional shift; the next
    // vacant-seat click on the new trip assigns them (Confirmed) to that seat
    const [selectedOptionalTraveller, setSelectedOptionalTraveller] = useState(null);
    // Stop/route mapping context: the transferring tickets' current stops and
    // the new trip's available stops/routes. The route resolves first (auto by
    // origin+destination, else user selection); within its origin/destination
    // city, stops auto-match by addressId and the rest must be mapped by the
    // user below. The resolved ids are sent with the transfer assignments.
    const [transferContext, setTransferContext] = useState(null);
    const [pickupSelections, setPickupSelections] = useState({});
    const [dropSelections, setDropSelections] = useState({});
    const [routeSelections, setRouteSelections] = useState({});
    // Ref (not state) because the checkbox lives inside Modal.confirm content,
    // which does not re-render on state changes. Same behaviour as the
    // modify/cancel page's bus change Send Notification checkbox.
    const sendNotificationRef = useRef(true);

    if (!checkActionAllowed(VIEW_ADMIN_TAB_IN_SEARCH_PAGE)) {
        return null;
    }

    const setLoading = (bool) => dispatch(loadingAction(bool));

    const resetState = () => {
        form.resetFields();
        setAvailableTrips([]);
        setSelectedNewTrip(null);
        setSearched(false);
        setOldSeats([]);
        setNewSeats([]);
        setSelectedOldSeat(null);
        setMappings([]);
        resetStopMappingState();
    };

    const resetStopMappingState = () => {
        setTransferContext(null);
        setPickupSelections({});
        setDropSelections({});
        setRouteSelections({});
        setSelectedOptionalTraveller(null);
    };

    const showModal = () => setIsModalVisible(true);

    const handleCancel = () => {
        setIsModalVisible(false);
        resetState();
    };

    const handleSearch = () => {
        form.validateFields(['newTripDate']).then((values) => {
            let date = getAPIDateFormat(values.newTripDate);
            setLoading(true);
            dispatch(fetchSchedules(
                schedule.origin,
                schedule.destination,
                date,
                false,
                true,
                (schedules) => {
                    setLoading(false);
                    setSearched(true);
                    let list = isValidArray(schedules) ? schedules : [];
                    let filtered = list.filter((item) => {
                        return isValid(item) && isValid(item.trip) && item.trip.id !== schedule.trip.id;
                    });
                    setAvailableTrips(filtered);
                    setSelectedNewTrip(null);
                    setOldSeats([]);
                    setNewSeats([]);
                    setMappings([]);
                    setSelectedOldSeat(null);
                    form.setFieldsValue({ newTripId: undefined });
                }
            ));
        }).catch(() => {});
    };

    const loadBothSeatLayouts = (newTripItem) => {
        setLoading(true);
        resetStopMappingState();
        getChartTransferMappingDetails(schedule.trip.id, newTripItem.trip.id, (context) => {
            setTransferContext(context);
        }, (errMsg) => {
            message.error(isValid(errMsg) ? errMsg : 'Failed to load stop mapping details');
        });
        Promise.all([
            fetchSeatLayoutDirect(schedule.trip_route_id),
            fetchSeatLayoutDirect(newTripItem.trip_route_id)
        ]).then(([oldPayload, newPayload]) => {
            setOldSeats(isValidArray(oldPayload.seats) ? oldPayload.seats : []);
            setNewSeats(isValidArray(newPayload.seats) ? newPayload.seats : []);
            setMappings([]);
            setSelectedOldSeat(null);
            setLoading(false);
        }).catch((err) => {
            setLoading(false);
            message.error(err.message || 'Failed to load seat layouts');
        });
    };

    const handleNewTripChange = (tripId) => {
        const trip = availableTrips.find((t) => t.trip.id === tripId);
        setSelectedNewTrip(trip || null);
        if (trip) {
            loadBothSeatLayouts(trip);
        } else {
            setOldSeats([]);
            setNewSeats([]);
            setMappings([]);
            setSelectedOldSeat(null);
            resetStopMappingState();
        }
    };

    /*
      Match the transferring tickets' stops/routes against the new trip.
      The route resolves first (auto by origin+destination, else the user's
      selection); its origin/destination city then scopes the boarding/drop
      options - only the points of that city are offered, and an auto stop
      match (same addressId) counts only when the stop is in that city. The
      transfer is blocked until every unmatched route and stop has a selection.
    */
    const getStopMappingInfo = () => {
        if (!isValid(transferContext)) {
            return null;
        }
        const ticketStops = isValidArray(transferContext.ticketStops) ? transferContext.ticketStops : [];
        const newPickups = isValidArray(transferContext.newPickups) ? transferContext.newPickups : [];
        const newDrops = isValidArray(transferContext.newDrops) ? transferContext.newDrops : [];
        const newRoutes = isValidArray(transferContext.newRoutes) ? transferContext.newRoutes : [];
        const stopsByTicket = {};
        ticketStops.forEach((stops) => { stopsByTicket[stops.ticketId] = stops; });
        const sameCity = (cityA, cityB) => isValidString(cityA) && isValidString(cityB)
            && cityA.toLowerCase() == cityB.toLowerCase();
        const routeKeyOf = (stops) => `${stops.origin}|${stops.destination}`;
        const findRouteMatch = (origin, destination) =>
            newRoutes.find((route) => route.origin == origin && route.destination == destination);
        const getRouteById = (tripRouteId) => isValid(tripRouteId)
            ? newRoutes.find((route) => route.tripRouteId == tripRouteId) : null;
        // The route the ticket will use on the new trip: auto-matched when the
        // same origin-destination exists, else the user's selection (null until chosen)
        const resolveRoute = (stops) => {
            const match = findRouteMatch(stops.origin, stops.destination);
            return isValid(match) ? match : getRouteById(routeSelections[routeKeyOf(stops)]);
        };
        const pickupOptionsForCity = (city) => newPickups.filter((stop) => sameCity(stop.city, city));
        const dropOptionsForCity = (city) => newDrops.filter((stop) => sameCity(stop.city, city));
        const findPickupMatch = (stops, route) => isValid(route) && isValid(stops.pickupAddressId)
            ? pickupOptionsForCity(route.origin).find((stop) => stop.addressId == stops.pickupAddressId) : null;
        const findDropMatch = (stops, route) => isValid(route) && isValid(stops.dropAddressId)
            ? dropOptionsForCity(route.destination).find((stop) => stop.addressId == stops.dropAddressId) : null;
        const unmatchedPickups = [], unmatchedDrops = [], unmatchedRoutes = [];
        const seenPickups = new Set(), seenDrops = new Set(), seenRoutes = new Set();
        Array.from(new Set(mappings.map((m) => m.ticketId))).forEach((ticketId) => {
            const stops = stopsByTicket[ticketId];
            if (!isValid(stops)) {
                return;
            }
            const routeKey = routeKeyOf(stops);
            if (!isValid(findRouteMatch(stops.origin, stops.destination)) && !seenRoutes.has(routeKey)) {
                seenRoutes.add(routeKey);
                unmatchedRoutes.push({ routeKey, origin: stops.origin, destination: stops.destination });
            }
            const route = resolveRoute(stops);
            const pickupKey = `${stops.pickupId}|${routeKey}`;
            if (isValid(stops.pickupId) && !isValid(findPickupMatch(stops, route)) && !seenPickups.has(pickupKey)) {
                seenPickups.add(pickupKey);
                unmatchedPickups.push({ ...stops, selectionKey: pickupKey, routeKey, route });
            }
            const dropKey = `${stops.dropId}|${routeKey}`;
            if (isValid(stops.dropId) && !isValid(findDropMatch(stops, route)) && !seenDrops.has(dropKey)) {
                seenDrops.add(dropKey);
                unmatchedDrops.push({ ...stops, selectionKey: dropKey, routeKey, route });
            }
        });
        return {
            stopsByTicket, newRoutes,
            routeKeyOf, findRouteMatch, getRouteById, resolveRoute,
            pickupOptionsForCity, dropOptionsForCity, findPickupMatch, findDropMatch,
            unmatchedPickups, unmatchedDrops, unmatchedRoutes
        };
    };

    const stopMappingInfo = getStopMappingInfo();

    /*
      One row per optional (not-boarded / extra-seat) passenger with explicit
      actions: Assign Seat arms the next vacant-seat click on the new trip,
      Move as Extra (extra-seat only) shifts them without a seat. Mapped rows
      turn green with their outcome; an armed row turns amber until a seat is
      picked or the action is cancelled.
    */
    const renderOptionalTravellerRow = (traveller) => {
        const mapped = mappings.find((m) => m.travellerId === traveller.travellerId);
        const isSelected = isValid(selectedOptionalTraveller)
            && selectedOptionalTraveller.travellerId === traveller.travellerId;
        const isNotBoarded = traveller.type === 'notBoarded';
        return (
            <div
                key={`opt-${traveller.travellerId}`}
                className={`ct-optional-row${mapped ? ' ct-optional-mapped' : ''}${isSelected ? ' ct-optional-armed' : ''}`}
            >
                <span className="ct-optional-name">
                    {traveller.fullName || 'Passenger'}{traveller.gender ? ` (${traveller.gender})` : ''}
                </span>
                <span className="ct-optional-meta">
                    {isNotBoarded ? `was seat ${traveller.seatNumber}` : 'no seat on layout'}
                </span>
                <span className="ct-optional-actions">
                    {mapped ? (
                        <>
                            <Tag color="green" style={{ margin: 0 }}>
                                {isValid(mapped.newSeatNumber)
                                    ? `→ Seat ${mapped.newSeatNumber} • Confirmed`
                                    : '→ Moves as extra (no seat)'}
                            </Tag>
                            <Button size="small" onClick={() => removeOptionalMapping(traveller.travellerId)}>Remove</Button>
                        </>
                    ) : isSelected ? (
                        <>
                            <Tag color="blue" style={{ margin: 0 }}>Click a vacant seat on the New Trip layout…</Tag>
                            <Button size="small" onClick={() => setSelectedOptionalTraveller(null)}>Cancel</Button>
                        </>
                    ) : (
                        <>
                            <Button
                                size="small"
                                type="primary"
                                ghost
                                onClick={() => {
                                    setSelectedOldSeat(null);
                                    setSelectedOptionalTraveller(traveller);
                                }}
                            >
                                Assign Seat
                            </Button>
                            {!isNotBoarded && (
                                <Button
                                    size="small"
                                    onClick={() => {
                                        setSelectedOptionalTraveller(null);
                                        setMappings([
                                            ...mappings,
                                            {
                                                assignmentType: 'extraSeat',
                                                travellerId: traveller.travellerId,
                                                ticketId: traveller.ticketId,
                                                oldSeatNumber: traveller.seatNumber,
                                                newSeatNumber: null,
                                                gender: traveller.gender,
                                                ticketStatus: 'Confirmed'
                                            }
                                        ]);
                                    }}
                                >
                                    Move as Extra
                                </Button>
                            )}
                        </>
                    )}
                </span>
            </div>
        );
    };

    const isOptionalMapping = (m) => isValid(m.assignmentType) && m.assignmentType !== 'seat';

    const onOldSeatClick = (seat) => {
        if (!isOccupiedSeat(seat)) {
            message.info('Pick a passenger seat (Confirmed/Blocked) on the source trip.');
            return;
        }
        const existing = mappings.find((m) => !isOptionalMapping(m) && m.oldSeatNumber === seat.seat_number);
        if (existing) {
            setMappings(mappings.filter((m) => isOptionalMapping(m) || m.oldSeatNumber !== seat.seat_number));
            if (selectedOldSeat && selectedOldSeat.seat_number === seat.seat_number) {
                setSelectedOldSeat(null);
            }
            return;
        }
        setSelectedOptionalTraveller(null);
        setSelectedOldSeat(seat);
    };

    const onNewSeatClick = (seat) => {
        const existingTarget = mappings.find((m) => m.newSeatNumber === seat.seat_number);
        if (existingTarget) {
            setMappings(mappings.filter((m) => m.newSeatNumber !== seat.seat_number));
            return;
        }
        if (!isVacantSeat(seat)) {
            message.warning('Selected seat on the new trip is not vacant.');
            return;
        }
        if (selectedOptionalTraveller) {
            // Optional shift: extra-seat / not-boarded passenger lands on this
            // seat as Confirmed
            setMappings([
                ...mappings,
                {
                    assignmentType: selectedOptionalTraveller.type,
                    travellerId: selectedOptionalTraveller.travellerId,
                    ticketId: selectedOptionalTraveller.ticketId,
                    oldSeatNumber: selectedOptionalTraveller.seatNumber,
                    newSeatNumber: seat.seat_number,
                    gender: selectedOptionalTraveller.gender,
                    ticketStatus: 'Confirmed'
                }
            ]);
            setSelectedOptionalTraveller(null);
            return;
        }
        if (!selectedOldSeat) {
            message.info('First pick a passenger seat on the source trip, then click a vacant seat on the new trip.');
            return;
        }
        setMappings([
            ...mappings,
            {
                ticketId: selectedOldSeat.ticket_id,
                oldSeatNumber: selectedOldSeat.seat_number,
                newSeatNumber: seat.seat_number,
                gender: selectedOldSeat.gender,
                ticketStatus: selectedOldSeat.seat_status
            }
        ]);
        setSelectedOldSeat(null);
    };

    const removeMapping = (oldSeatNumber) => {
        setMappings(mappings.filter((m) => isOptionalMapping(m) || m.oldSeatNumber !== oldSeatNumber));
    };

    const removeOptionalMapping = (travellerId) => {
        setMappings(mappings.filter((m) => m.travellerId !== travellerId));
    };

    /*
      Auto-assign all confirmed/blocked passengers to the new trip using the provided
      target-finder. Processes per ticket to honour the partial-ticket rule — a ticket
      is only added if ALL of its unmapped seats can be placed. Returns summary counts.
    */
    const autoAssign = (findTargetSeat, strategyLabel) => {
        if (!isValidArrayWithValues(oldSeats) || !isValidArrayWithValues(newSeats)) {
            message.info('Seat layouts not yet loaded.');
            return;
        }
        const sourceOccupied = oldSeats.filter(isOccupiedSeat);
        if (sourceOccupied.length === 0) {
            message.info('No confirmed/blocked passengers to transfer.');
            return;
        }
        const usedNewSeats = new Set(mappings.map((m) => m.newSeatNumber));
        // Only regular seat mappings block a source seat - an optional
        // (extra/not-boarded) mapping's old seat may be occupied by a
        // different, still-transferable ticket on the source layout
        const usedOldSeats = new Set(mappings.filter((m) => !isOptionalMapping(m)).map((m) => m.oldSeatNumber));
        const byTicket = {};
        sourceOccupied.forEach((s) => {
            if (usedOldSeats.has(s.seat_number)) return;
            if (!byTicket[s.ticket_id]) byTicket[s.ticket_id] = [];
            byTicket[s.ticket_id].push(s);
        });

        const addedMappings = [];
        const skippedTickets = [];
        Object.keys(byTicket).forEach((ticketId) => {
            const ticketSeats = byTicket[ticketId];
            const candidate = [];
            let ok = true;
            for (const src of ticketSeats) {
                const target = findTargetSeat(src, usedNewSeats);
                if (!target) { ok = false; break; }
                candidate.push({
                    ticketId: src.ticket_id,
                    oldSeatNumber: src.seat_number,
                    newSeatNumber: target.seat_number,
                    gender: src.gender,
                    ticketStatus: src.seat_status
                });
                usedNewSeats.add(target.seat_number);
            }
            if (ok) {
                addedMappings.push(...candidate);
            } else {
                candidate.forEach((c) => usedNewSeats.delete(c.newSeatNumber));
                skippedTickets.push(ticketId);
            }
        });

        if (addedMappings.length === 0) {
            message.warning(`No passengers could be placed by ${strategyLabel}.`);
            return;
        }
        setMappings([...mappings, ...addedMappings]);
        setSelectedOldSeat(null);
        if (skippedTickets.length > 0) {
            message.warning(`Placed ${addedMappings.length} seat(s). Skipped ${skippedTickets.length} ticket(s) where no matching target seat was available.`);
        } else {
            message.success(`Auto-assigned ${addedMappings.length} seat(s) by ${strategyLabel}.`);
        }
    };

    const autoAssignBySeatNumber = () => {
        autoAssign((src, usedNewSeats) => {
            return newSeats.find((ns) =>
                ns.seat_number === src.seat_number &&
                !usedNewSeats.has(ns.seat_number) &&
                isVacantSeat(ns)
            );
        }, 'seat number');
    };

    const autoAssignByRowCol = () => {
        autoAssign((src, usedNewSeats) => {
            return newSeats.find((ns) =>
                ns.seat_row_number === src.seat_row_number &&
                ns.seat_column_number === src.seat_column_number &&
                (ns.upper_deck || 0) === (src.upper_deck || 0) &&
                !usedNewSeats.has(ns.seat_number) &&
                isVacantSeat(ns)
            );
        }, 'row & column');
    };

    const handleTransfer = () => {
        if (!selectedNewTrip) {
            message.warning('Please select a new trip.');
            return;
        }
        if (!isValidArrayWithValues(mappings)) {
            message.warning('Please assign at least one passenger to a new seat.');
            return;
        }
        // Block partial-ticket transfers: every confirmed/blocked seat belonging to a
        // touched ticket must be in the mappings list. Otherwise the ticket would have
        // its trip_id moved to the new trip while some of its travellers stay on the
        // old trip's seats — leaving the data inconsistent.
        const touchedTicketIds = Array.from(new Set(mappings.map((m) => m.ticketId)));
        const partialTickets = [];
        touchedTicketIds.forEach((ticketId) => {
            const ticketSeats = oldSeats.filter((s) => s.ticket_id === ticketId && isOccupiedSeat(s));
            // Optional (extra/not-boarded) mappings are not layout seats -
            // every SEATED passenger of a touched ticket must still be mapped
            const mappedSeatsForTicket = mappings.filter((m) => m.ticketId === ticketId && !isOptionalMapping(m));
            if (ticketSeats.length > mappedSeatsForTicket.length) {
                const mappedSet = new Set(mappedSeatsForTicket.map((m) => m.oldSeatNumber));
                const missing = ticketSeats
                    .map((s) => s.seat_number)
                    .filter((n) => !mappedSet.has(n));
                partialTickets.push({ ticketId, missing });
            }
        });
        if (partialTickets.length > 0) {
            const detail = partialTickets
                .map((p) => `ticket ${p.ticketId} (missing seat${p.missing.length > 1 ? 's' : ''} ${p.missing.join(', ')})`)
                .join('; ');
            message.error(`Partial ticket transfer is not allowed. Map all seats of: ${detail}`);
            return;
        }
        // Every route/stop that has no equivalent on the new trip must have a
        // user-selected replacement before the transfer can proceed. Stops of a
        // still-unselected route are unresolvable and count as missing too.
        if (isValid(stopMappingInfo)) {
            const missingRoutes = stopMappingInfo.unmatchedRoutes.filter((route) => !isValid(routeSelections[route.routeKey]));
            const missingPickups = stopMappingInfo.unmatchedPickups.filter((stops) => !isValid(pickupSelections[stops.selectionKey]));
            const missingDrops = stopMappingInfo.unmatchedDrops.filter((stops) => !isValid(dropSelections[stops.selectionKey]));
            if (missingRoutes.length > 0 || missingPickups.length > 0 || missingDrops.length > 0) {
                message.error('Some routes or boarding/dropping points are not available on the new trip. Select replacements in the "Map Stops & Routes to New Trip" section first.');
                return;
            }
        }
        // Send the resolved new-trip route/stop ids with each assignment:
        // auto-matched values when the same route/stop exists on the new chart,
        // otherwise the user's selection from the mapping section.
        const assignmentsWithStops = mappings.map((m) => {
            const stops = isValid(stopMappingInfo) ? stopMappingInfo.stopsByTicket[m.ticketId] : null;
            if (!isValid(stops)) {
                return m;
            }
            const routeKey = stopMappingInfo.routeKeyOf(stops);
            const route = stopMappingInfo.resolveRoute(stops);
            const pickupMatch = stopMappingInfo.findPickupMatch(stops, route);
            const dropMatch = stopMappingInfo.findDropMatch(stops, route);
            return {
                ...m,
                newTripRouteId: isValid(route) ? route.tripRouteId : undefined,
                newPickupId: isValid(pickupMatch) ? pickupMatch.id : pickupSelections[`${stops.pickupId}|${routeKey}`],
                newDropId: isValid(dropMatch) ? dropMatch.id : dropSelections[`${stops.dropId}|${routeKey}`]
            };
        });
        showConfirm(() => {
            setLoading(true);
            transferChartToNewTrip(
                schedule.trip.id,
                selectedNewTrip.trip.id,
                assignmentsWithStops,
                sendNotificationRef.current,
                (msg, payload) => {
                    setLoading(false);
                    let summary = '';
                    if (isValid(payload)) {
                        const t = isValidArray(payload.transferred) ? payload.transferred.length : 0;
                        const s = isValidArray(payload.skipped) ? payload.skipped.length : 0;
                        summary = ` (${t} transferred${s > 0 ? `, ${s} skipped` : ''})`;
                        if (s > 0) {
                            const skippedSeats = payload.skipped.map((x) => `${x.oldSeatNumber}→${x.newSeatNumber}`).join(', ');
                            message.warning(`Skipped: ${skippedSeats}`);
                        }
                    }
                    message.success((isValid(msg) ? msg : 'Chart transferred') + summary);
                    setIsModalVisible(false);
                    resetState();
                },
                (errMsg) => {
                    setLoading(false);
                    message.error(isValid(errMsg) ? errMsg : 'Failed to transfer chart');
                }
            );
        },
        'Confirm Chart Transfer',
        <div>
            <div>{`Transfer ${mappings.length} passenger(s) to the selected new trip? This action cannot be undone.`}</div>
            <Checkbox
                defaultChecked={sendNotificationRef.current}
                onChange={(e)=>{sendNotificationRef.current = e.target.checked}}
                style={{ marginTop: 8 }}
            >
                Send Notification
            </Checkbox>
        </div>,
        'Yes, Transfer');
    };

    let sourceDate = isValid(schedule) && isValid(schedule.trip) && isValid(schedule.trip.start_date)
        ? moment(schedule.trip.start_date) : moment();

    const renderTripOptions = () => {
        return availableTrips.map((item) => {
            let label = `${item.description}`;
            if (isValid(item.trip) && isValid(item.trip.start_date)) {
                label += ` - ${getLocalDate(new Date(item.trip.start_date))}`;
            }
            return (
                <Option key={item.trip.id} value={item.trip.id}>{label}</Option>
            );
        });
    };

    return (
        <div>
            <Button
                className="bordered-btn send-driver-details-btn"
                onClick={showModal}
            >
                Chart Transfer
            </Button>
            <Modal
                className="chart-transfer-modal"
                title="Chart Transfer"
                visible={isModalVisible}
                onCancel={handleCancel}
                footer={[
                    <Button key="exit" className="exit-btn" onClick={handleCancel}>Exit</Button>,
                    <Button key="transfer" type="primary" onClick={handleTransfer} disabled={!selectedNewTrip || mappings.length === 0}>
                        Transfer Chart{mappings.length > 0 ? ` (${mappings.length})` : ''}
                    </Button>
                ]}
                width="90%"
                style={{ top: 20 }}
                destroyOnClose
            >
                <div className="ct-scroll-area">
                <div className="ct-trip-info">
                    <Text strong>From: </Text>
                    <Text>{schedule.description} ({getLocalDate(new Date(schedule.trip.start_date))})</Text>
                </div>
                <Form form={form} layout="vertical" initialValues={{ newTripDate: sourceDate }}>
                    <Row gutter={16} align="bottom">
                        <Col span={8}>
                            <Form.Item
                                name="newTripDate"
                                label="New Trip Date"
                                rules={[{ required: true, message: 'Please select a date' }]}
                            >
                                <DatePicker
                                    style={{ width: '100%' }}
                                    format="DD-MM-YYYY"
                                    disabledDate={(current) => current && current < moment().startOf('day')}
                                />
                            </Form.Item>
                        </Col>
                        <Col>
                            <Form.Item>
                                <Button onClick={handleSearch}>Search Trips</Button>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="newTripId"
                                label="Select New Trip"
                                rules={[{ required: true, message: 'Please select a trip' }]}
                            >
                                <Select
                                    placeholder={searched ? (availableTrips.length > 0 ? 'Select trip' : 'No other trips available') : 'Search trips first'}
                                    onChange={handleNewTripChange}
                                    disabled={!searched || availableTrips.length === 0}
                                    showSearch
                                    optionFilterProp="children"
                                >
                                    {renderTripOptions()}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>

                {selectedNewTrip && (
                    <>
                        <div className="ct-helper-text">
                            Click a passenger seat on the source trip, then click a vacant seat on the new trip to assign.
                            To remove a mapping, click the mapped passenger seat on the source trip or the × on its tag below.
                        </div>
                        {isValid(transferContext) && isValidArrayWithValues(transferContext.optionalTravellers) && (
                            <div className="ct-optional-section">
                                <div className="ct-section-title">Optional Passengers</div>
                                <div className="ct-helper-text">
                                    Shifting these passengers is optional - anything left unmapped keeps its
                                    current state: extra-seat passengers follow their ticket without a seat,
                                    not-boarded passengers stay unchanged on the source trip.
                                </div>
                                {transferContext.optionalTravellers.some((traveller) => traveller.type === 'notBoarded') && (
                                    <>
                                        <div className="ct-optional-group-title">Not Boarded</div>
                                        <div className="ct-helper-text">
                                            Assign a vacant seat on the new trip - the passenger is re-activated
                                            as Confirmed on that seat.
                                        </div>
                                        {transferContext.optionalTravellers
                                            .filter((traveller) => traveller.type === 'notBoarded')
                                            .map(renderOptionalTravellerRow)}
                                    </>
                                )}
                                {transferContext.optionalTravellers.some((traveller) => traveller.type === 'extraSeat') && (
                                    <>
                                        <div className="ct-optional-group-title">Extra Seats</div>
                                        <div className="ct-helper-text">
                                            Give the passenger a real seat on the new trip, or move them with
                                            their ticket still as an extra seat.
                                        </div>
                                        {transferContext.optionalTravellers
                                            .filter((traveller) => traveller.type === 'extraSeat')
                                            .map(renderOptionalTravellerRow)}
                                    </>
                                )}
                            </div>
                        )}
                        <DndProvider backend={HTML5Backend}>
                            <div className="ct-grids-row">
                                <div className="ct-grid-col">
                                    <div className="ct-section-title">
                                        Source Trip{selectedOldSeat ? ` — selected: ${selectedOldSeat.seat_number}` : ''}
                                    </div>
                                    <SeatLayoutPanel
                                        seats={oldSeats}
                                        role="old"
                                        selectedOldSeat={selectedOldSeat}
                                        mappings={mappings}
                                        onSeatClick={onOldSeatClick}
                                    />
                                </div>
                                <div className="ct-bulk-toolbar">
                                    <Tooltip title="Auto-assign all passengers by matching seat number" placement="left">
                                        <Button
                                            shape="circle"
                                            icon={<FieldNumberOutlined />}
                                            onClick={autoAssignBySeatNumber}
                                        />
                                    </Tooltip>
                                    <Tooltip title="Auto-assign all passengers by matching row & column" placement="left">
                                        <Button
                                            shape="circle"
                                            icon={<TableOutlined />}
                                            onClick={autoAssignByRowCol}
                                        />
                                    </Tooltip>
                                </div>
                                <div className="ct-grid-col">
                                    <div className="ct-section-title">New Trip</div>
                                    <SeatLayoutPanel
                                        seats={newSeats}
                                        role="new"
                                        selectedOldSeat={selectedOldSeat}
                                        mappings={mappings}
                                        onSeatClick={onNewSeatClick}
                                    />
                                </div>
                            </div>
                        </DndProvider>
                        <SeatLegend />

                        <div className="ct-mapping-list">
                            <div className="ct-section-title">Pending Assignments ({mappings.length})</div>
                            {mappings.length === 0 ? (
                                <div className="ct-helper-text">No assignments yet.</div>
                            ) : (
                                mappings.map((m) => (
                                    <Tag
                                        key={isOptionalMapping(m) ? `opt-${m.travellerId}` : m.oldSeatNumber}
                                        closable
                                        onClose={(e) => {
                                            e.preventDefault();
                                            if (isOptionalMapping(m)) {
                                                removeOptionalMapping(m.travellerId);
                                            } else {
                                                removeMapping(m.oldSeatNumber);
                                            }
                                        }}
                                        color={isOptionalMapping(m) ? 'orange' : 'blue'}
                                        style={{ marginBottom: 4 }}
                                    >
                                        {m.oldSeatNumber}{m.gender ? ` (${m.gender})` : ''} → {m.newSeatNumber || 'extra (no seat)'}
                                        {m.ticketStatus === 'Blocked' ? ' • Blocked' : ''}
                                        {m.assignmentType === 'notBoarded' ? ' • Not boarded' : ''}
                                        {m.assignmentType === 'extraSeat' ? ' • Extra seat' : ''}
                                    </Tag>
                                ))
                            )}
                        </div>

                        {isValid(stopMappingInfo) &&
                            (stopMappingInfo.unmatchedPickups.length > 0
                                || stopMappingInfo.unmatchedDrops.length > 0
                                || stopMappingInfo.unmatchedRoutes.length > 0) && (
                            <div className="ct-mapping-list">
                                <div className="ct-section-title">Map Stops &amp; Routes to New Trip</div>
                                <div className="ct-helper-text">
                                    These routes or boarding/dropping points of the transferring tickets are not
                                    available on the new trip. Select the route first - the boarding/dropping
                                    choices then show only that route's origin/destination city points.
                                </div>
                                {stopMappingInfo.unmatchedRoutes.map((route) => (
                                    <Row key={`route-${route.routeKey}`} gutter={8} align="middle" style={{ marginBottom: 8 }}>
                                        <Col>
                                            <Text strong>Route: </Text>
                                            <Text>{route.origin} → {route.destination} →</Text>
                                        </Col>
                                        <Col>
                                            <Select
                                                style={{ minWidth: 240 }}
                                                placeholder="Select route on new trip"
                                                value={routeSelections[route.routeKey]}
                                                onChange={(value) => {
                                                    setRouteSelections({ ...routeSelections, [route.routeKey]: value });
                                                    // The origin/destination city changed - discard stop
                                                    // selections made for the previous route choice
                                                    const clearDependentSelections = (selections) => {
                                                        const next = { ...selections };
                                                        Object.keys(next).forEach((selectionKey) => {
                                                            if (selectionKey.endsWith(`|${route.routeKey}`)) {
                                                                delete next[selectionKey];
                                                            }
                                                        });
                                                        return next;
                                                    };
                                                    setPickupSelections(clearDependentSelections(pickupSelections));
                                                    setDropSelections(clearDependentSelections(dropSelections));
                                                }}
                                                showSearch
                                                optionFilterProp="children"
                                            >
                                                {stopMappingInfo.newRoutes.map((newRoute) => (
                                                    <Option key={newRoute.tripRouteId} value={newRoute.tripRouteId}>
                                                        {`${newRoute.origin} → ${newRoute.destination}`}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Col>
                                    </Row>
                                ))}
                                {stopMappingInfo.unmatchedPickups.map((stops) => (
                                    <Row key={`pickup-${stops.selectionKey}`} gutter={8} align="middle" style={{ marginBottom: 8 }}>
                                        <Col>
                                            <Text strong>Boarding: </Text>
                                            <Text>{stops.pickupLocation || `#${stops.pickupId}`}{stops.pickupTiming ? ` (${stops.pickupTiming})` : ''} →</Text>
                                        </Col>
                                        <Col>
                                            <Select
                                                style={{ minWidth: 240 }}
                                                placeholder={isValid(stops.route)
                                                    ? `Select boarding point in ${stops.route.origin}`
                                                    : 'Select route first'}
                                                disabled={!isValid(stops.route)}
                                                value={pickupSelections[stops.selectionKey]}
                                                onChange={(value) => setPickupSelections({ ...pickupSelections, [stops.selectionKey]: value })}
                                                showSearch
                                                optionFilterProp="children"
                                            >
                                                {(isValid(stops.route) ? stopMappingInfo.pickupOptionsForCity(stops.route.origin) : []).map((stop) => (
                                                    <Option key={stop.id} value={stop.id}>
                                                        {`${stop.location || `#${stop.id}`}${stop.timing ? ` (${stop.timing})` : ''}`}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Col>
                                    </Row>
                                ))}
                                {stopMappingInfo.unmatchedDrops.map((stops) => (
                                    <Row key={`drop-${stops.selectionKey}`} gutter={8} align="middle" style={{ marginBottom: 8 }}>
                                        <Col>
                                            <Text strong>Dropping: </Text>
                                            <Text>{stops.dropLocation || `#${stops.dropId}`}{stops.dropTiming ? ` (${stops.dropTiming})` : ''} →</Text>
                                        </Col>
                                        <Col>
                                            <Select
                                                style={{ minWidth: 240 }}
                                                placeholder={isValid(stops.route)
                                                    ? `Select dropping point in ${stops.route.destination}`
                                                    : 'Select route first'}
                                                disabled={!isValid(stops.route)}
                                                value={dropSelections[stops.selectionKey]}
                                                onChange={(value) => setDropSelections({ ...dropSelections, [stops.selectionKey]: value })}
                                                showSearch
                                                optionFilterProp="children"
                                            >
                                                {(isValid(stops.route) ? stopMappingInfo.dropOptionsForCity(stops.route.destination) : []).map((stop) => (
                                                    <Option key={stop.id} value={stop.id}>
                                                        {`${stop.location || `#${stop.id}`}${stop.timing ? ` (${stop.timing})` : ''}`}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Col>
                                    </Row>
                                ))}
                            </div>
                        )}
                    </>
                )}
                </div>
            </Modal>
        </div>
    );
};
